import React, { useState } from "react";
import { DocWrapper } from "./DocStyles";

const Card = ({ id, dot, title, isOpen, onToggle, children }) => (
  <div className="card">
    <div className="card-header" onClick={() => onToggle(id)}>
      <span className={`arrow ${isOpen ? 'open' : ''}`}>▶</span>
      <span className={`card-dot ${dot}`}></span>
      <h3>{title}</h3>
    </div>
    <div className={`card-body ${isOpen ? 'open' : 'collapsed'}`}>
      {children}
    </div>
  </div>
);

export default function PlatformOverview() {
  const [openCards, setOpenCards] = useState({
    mission: true,
    contact: true,
    market: true,
    stats: true,
    apps: false,
    plans: false,
    features: false,
    flow: false,
    gate: false,
    revenue: false,
    acquisition: false,
    competitors: false,
    advantages: false,
    tech_arch: false,
    external: false,
    db_arch: false,
    roles: false,
    roadmap: false,
    age_rules: false,
    matching: false,
    privacy: false,
    carer_notes: false
  });

  const toggleCard = (id) => {
    setOpenCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <DocWrapper>
      <div className="header">
        <h1>B4T Platform Overview & Company Handbook</h1>
        <div className="tagline">Buddy4Travel — the complete guide for stakeholders, partners, and the team</div>
        <div className="subtitle">Company overview · Product · Marketplace · Revenue · Technology · Strategy</div>
      </div>

      {/* ===== SECTION 1: COMPANY OVERVIEW ===== */}
      <div className="section-hdr"> 1. Company Overview <span className="tag">Who we are</span></div>

      <Card id="mission" isOpen={openCards["mission"]} onToggle={toggleCard} dot="cyan" title="Mission & Vision">
        <div className="quote">"To ensure no one travels through an airport alone, uncertain, or unsupported — by connecting every Care Seeker with a trained, verified Carer who makes their journey safe, comfortable, and human."</div>
        <p><strong>What we do:</strong> Buddy4Travel (B4T) operates a two-sided marketplace that connects Care Seekers who need in-person airport assistance ("Care Seekers") with trained Carers ("Carers") who accompany them through the entire airport journey — from curb to gate to destination.</p>
        <p><strong>Why it matters:</strong> Air travel can be overwhelming and physically challenging for elderly Care Seekers, unaccompanied minors, first-time flyers, people with disabilities, and anyone who finds navigating large airports alone difficult. Existing solutions are fragmented — airline wheelchair services are limited, airport concierges are expensive, and freelance Carers are unregulated. B4T provides a <strong>managed, quality-controlled marketplace</strong> that solves this gap.</p>
        <div className="highlight-box"><strong>Our tagline:</strong> "Care in the Air" — B4T serves seniors, disabled travellers, first-time flyers, anxious travellers, non-native speakers, families, solo travellers, and anyone who requires additional assistance. Our peer-to-peer service ensures a seamless and secure experience for all.</div>
      </Card>

      <Card id="contact" isOpen={openCards["contact"]} onToggle={toggleCard} dot="cyan" title="Official Contact Information & App Links">
        <div className="grid-2">
          <div>
            <strong>Contact Channels:</strong>
            <table>
              <thead>
                <tr><th>Region</th><th>Contact</th></tr>
              </thead>
              <tbody>
                <tr><td>Email</td><td>contact@b4t.com</td></tr>
                <tr><td>WhatsApp (Global)</td><td>+61 424 332 248</td></tr>
                <tr><td>USA & Canada</td><td>+1 417 900 3248</td></tr>
                <tr><td>United Kingdom</td><td>+44 7367 056 248</td></tr>
                <tr><td>Europe</td><td>+31 970 1027 5248</td></tr>
                <tr><td>India</td><td>+91 804 925 1243</td></tr>
                <tr><td>Australia & NZ</td><td>1800 248 785</td></tr>
                <tr><td>All Other Countries</td><td>+1 417 900 3248</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <strong>Download the App:</strong>
            <ul>
              <li><strong>Android:</strong> <a href="https://play.google.com/store/apps/details?id=com.buddy4.travel&hl=en" target="_blank" rel="noreferrer" style={{ color: "#004d2c" }}>Google Play Store</a></li>
              <li><strong>iOS:</strong> <a href="https://apps.apple.com/in/app/b4t-buddy4travel/id6737122842" target="_blank" rel="noreferrer" style={{ color: "#004d2c" }}>Apple App Store</a></li>
            </ul>
            <br />
            <strong>Additional Support Channels:</strong>
            <ul>
              <li>WhatsApp, Email, Regional toll-free phone lines</li>
              <li>Website contact form at b4t.com</li>
              <li>In-app support chat</li>
              <li>Feedback form on the website</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card id="market" isOpen={openCards["market"]} onToggle={toggleCard} dot="cyan" title="Market Opportunity">
        <div className="grid-2">
          <div>
            <strong>Addressable Market:</strong>
            <ul>
              <li>Elderly Care Seekers (65+) — growing demographic globally, highest need for airport assistance</li>
              <li>Unaccompanied minors — millions of children fly alone each year</li>
              <li>First-time flyers — anxiety about unknown airport procedures</li>
              <li>Care Seekers with disabilities — mobility, cognitive, or sensory challenges</li>
              <li>Corporate Care Seekers — companies needing reliable assistance for employees</li>
            </ul>
          </div>
          <div>
            <strong>Market Size:</strong>
            <ul>
              <li>Global air Care Seeker traffic: 4.5+ billion annually (pre-COVID) and growing</li>
              <li>Airport assistance services market: estimated $X billion, growing at Y% CAGR</li>
              <li>No dominant digital platform exists today — fragmented market of local services</li>
              <li>B4T is positioned as the first scalable, tech-enabled marketplace for airport companionship</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card id="stats" isOpen={openCards["stats"]} onToggle={toggleCard} dot="cyan" title="Platform Stats at a Glance">
        <div className="stat-row">
          <div className="stat"><div className="num">6</div><div className="label">Applications</div></div>
          <div className="stat"><div className="num">3</div><div className="label">Databases</div></div>
          <div className="stat"><div className="num">10</div><div className="label">Booking States</div></div>
          <div className="stat"><div className="num">3</div><div className="label">Service Plans</div></div>
          <div className="stat"><div className="num">46</div><div className="label">API Packages</div></div>
          <div className="stat"><div className="num">47</div><div className="label">Data Models</div></div>
          <div className="stat"><div className="num">5</div><div className="label">External APIs</div></div>
          <div className="stat"><div className="num">10</div><div className="label">Push Types</div></div>
        </div>
      </Card>

      {/* ===== SECTION 2: PRODUCT ===== */}
      <div className="section-hdr"> 2. Product Ecosystem <span className="tag">What we've built</span></div>

      <Card id="apps" isOpen={openCards["apps"]} onToggle={toggleCard} dot="cyan" title="The 6 Applications">
        <table>
          <thead>
            <tr><th>#</th><th>Application</th><th>Tech Stack</th><th>Audience</th><th>Production URL</th></tr>
          </thead>
          <tbody>
            <tr><td>1</td><td><strong>Consumer Mobile App</strong></td><td>React Native 0.75</td><td>Care Seekers (Care Seekers)</td><td>App Store / Play Store</td></tr>
            <tr><td>2</td><td><strong>Consumer API</strong></td><td>Node.js + Express + MongoDB</td><td>All consumers via API</td><td>api.b4t.com</td></tr>
            <tr><td>3</td><td><strong>Admin Dashboard</strong></td><td>React 18 + MUI + Redux</td><td>Internal operations team</td><td>Internal URL</td></tr>
            <tr><td>4</td><td><strong>Agent Mobile App</strong></td><td>Flutter + Riverpod</td><td>Carers and Agent Partners</td><td>App Store / Play Store</td></tr>
            <tr><td>5</td><td><strong>Agent Portal API</strong></td><td>Laravel 12 + MySQL + Redis</td><td>Agent apps via API</td><td>partner.b4t.com</td></tr>
            <tr><td>6</td><td><strong>Public Website</strong></td><td>Laravel + Vue.js + Vite</td><td>General public / marketing</td><td>b4t.com</td></tr>
          </tbody>
        </table>
      </Card>

      <Card id="plans" isOpen={openCards["plans"]} onToggle={toggleCard} dot="cyan" title="Service Plans — Basic, Medium, Premium">
        <table>
          <thead>
            <tr><th>Stage</th><th>Sub-Stage</th><th><span className="badge slate">Basic</span></th><th><span className="badge amber">Medium</span></th><th><span className="badge gold">Premium</span></th></tr>
          </thead>
          <tbody>
            <tr><td rowSpan="5"><strong>Pickup</strong></td><td>Meet at airport (pickup_user)</td><td>✅</td><td>✅</td><td>✅</td></tr>
            <tr><td>Entry to airport</td><td>✅</td><td>✅</td><td>✅</td></tr>
            <tr><td>Check-in at airline counter</td><td>—</td><td>✅</td><td>✅</td></tr>
            <tr><td>Security check assistance</td><td>—</td><td>—</td><td>✅</td></tr>
            <tr><td>Immigration check (intl)</td><td>—</td><td>—</td><td>✅</td></tr>
            <tr><td rowSpan="3"><strong>In Transit</strong></td><td>Guide to boarding gate</td><td>✅</td><td>✅</td><td>✅</td></tr>
            <tr><td>Boarding assistance</td><td>—</td><td>✅</td><td>✅</td></tr>
            <tr><td>In-flight companionship</td><td>—</td><td>—</td><td>✅</td></tr>
            <tr><td rowSpan="4"><strong>Arrival</strong></td><td>Landed / deplaning</td><td>—</td><td>✅</td><td>✅</td></tr>
            <tr><td>Baggage claim</td><td>—</td><td>—</td><td>✅</td></tr>
            <tr><td>Customs (intl)</td><td>—</td><td>—</td><td>✅</td></tr>
            <tr><td>Exit / handoff</td><td>✅</td><td>✅</td><td>✅</td></tr>
          </tbody>
        </table>
        <div className="highlight-box"><strong> Pricing strategy:</strong> Basic for budget-conscious confident Care Seekers. Medium covers the essentials for first-timers. Premium is the flagship — full door-to-door service for those who need and value complete support. Premium has the highest margin.</div>
      </Card>

      <Card id="features" isOpen={openCards["features"]} onToggle={toggleCard} dot="cyan" title="Key Features & Capabilities">
        <div className="grid-2">
          <div>
            <strong>For Care Seekers:</strong>
            <ul>
              <li>Flight-based booking with auto-fill via FlightAware</li>
              <li>Multiple plan tiers with transparent pricing</li>
              <li>Real-time journey tracking with GPS + photo proof</li>
              <li>In-app chat with assigned Carer</li>
              <li>Family member management (book for others)</li>
              <li>Saved payment methods for faster checkout</li>
              <li>Promo codes and referral rewards</li>
              <li>Rating and review system</li>
            </ul>
          </div>
          <div>
            <strong>For Carers:</strong>
            <ul>
              <li>Live Matchboard with interactive route map</li>
              <li>Flexible scheduling — pick flights matching existing travel</li>
              <li>KYC verification (Shufti Pro) for trust and safety</li>
              <li>Wallet with transaction history</li>
              <li>Bank account based payouts</li>
              <li>Performance reports and KPIs</li>
              <li>Tier-based rewards system</li>
              <li>Biometric login for security</li>
            </ul>
          </div>
        </div>
        <div className="grid-2" style={{ marginTop: "12px" }}>
          <div>
            <strong>For Admin / Operations:</strong>
            <ul>
              <li>Complete booking lifecycle management</li>
              <li>Carer assignment with approval gate</li>
              <li>Commission and pricing configuration</li>
              <li>Content management (medical issues, docs, professions, FAQs)</li>
              <li>Promo code and notification campaign management</li>
              <li>Sub-admin permission system (20+ modules)</li>
              <li>Checklist manager for journey stages</li>
              <li>Email history and audit trail</li>
            </ul>
          </div>
          <div>
            <strong>For Agent Partners:</strong>
            <ul>
              <li>Lead submission and conversion tracking</li>
              <li>Affiliate links with QR code sharing</li>
              <li>Reward and wallet management</li>
              <li>Performance reports with sparkline charts</li>
              <li>Document upload and agreement signing</li>
              <li>In-app notifications and FCM push</li>
              <li>Matchboard read-only view</li>
              <li>Multi-currency support</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* ===== SECTION 3: MARKETPLACE ===== */}
      <div className="section-hdr"> 3. How the Marketplace Works <span className="tag gold">Core flow</span></div>

      <Card id="flow" isOpen={openCards["flow"]} onToggle={toggleCard} dot="amber" title="End-to-End Booking Flow in 8 Steps">
        <div className="flow-step">
          <div className="flow-num cyan">1</div>
          <div className="flow-content"><h4>Care Seeker creates a booking</h4><p>Opens the Consumer Mobile App → enters flight number (auto-fills via FlightAware) → selects Care Seekers → chooses preferences (gender, language, medical) → picks a plan (Basic/Medium/Premium) → sees transparent fare breakdown → applies promo code (optional) → pays via Stripe (card/wallet) → booking confirmed.</p></div>
        </div>
        <div className="flow-step">
          <div className="flow-num emerald">2</div>
          <div className="flow-content"><h4>Booking goes live on Matchboard</h4><p>Payment captured by Stripe → Booking document created with status "booking_request_initiated" → Trail + OTP documents auto-created → Booking visible to all Carers on the same flight route. The Care Seeker waits.</p></div>
        </div>
        <div className="flow-step">
          <div className="flow-num cyan">3</div>
          <div className="flow-content"><h4>Carer finds and accepts the booking</h4><p>Carer opens Agent Mobile App → browses Matchboard with interactive world map → filters by route/date → sees Care Seeker details → adds own flight details → accepts the booking request → BookingRequest document created → admin notified.</p></div>
        </div>
        <div className="flow-step">
          <div className="flow-num amber">4</div>
          <div className="flow-content"><h4>Admin reviews and approves the assignment</h4><p>Admin logs into Dashboard → sees pending booking request → reviews Carer profile, flight match, pricing → clicks "Assign Carer" (or "Reject") → booking status becomes "carer_assigned" → checklist generated → chat enabled → both parties notified. <strong>This is the quality gate.</strong></p></div>
        </div>
        <div className="flow-step">
          <div className="flow-num cyan">5</div>
          <div className="flow-content"><h4>Pre-trip communication via chat</h4><p>Care Seeker and Carer message via in-app Socket.IO chat → coordinate meeting point, time, identifiers → 24h and 2h reminders sent via push notification → flight delay alerts handled automatically via FlightAware.</p></div>
        </div>
        <div className="flow-step">
          <div className="flow-num emerald">6</div>
          <div className="flow-content"><h4>Trip execution — tracked live</h4><p>Carer meets Seeker at airport → enters OTP to start the trip (proves physical presence) → progresses through journey stages (pickup → entry → check-in → security → boarding → etc.) → each stage requires GPS location + optional photo proof → Care Seeker sees live updates in real-time. Up to 12 stages for Premium.</p></div>
        </div>
        <div className="flow-step">
          <div className="flow-num cyan">7</div>
          <div className="flow-content"><h4>Trip ends, review submitted</h4><p>Carer taps "End Trip" → booking status "completed" → Care Seeker receives push → rates 1-5 stars with optional feedback + photo → rating influences Carer's standing.</p></div>
        </div>
        <div className="flow-step">
          <div className="flow-num amber">8</div>
          <div className="flow-content"><h4>Settlement — Carer gets paid</h4><p>Admin triggers "Amount Distribute" → system calculates: Carer earnings = subtotal - (subtotal × commission%) → Stripe payout initiated → Wallet credited → Transaction recorded. Carer can withdraw to their bank account.</p></div>
        </div>
      </Card>

      <Card id="gate" isOpen={openCards["gate"]} onToggle={toggleCard} dot="amber" title="The Approval Gate — Why Admin Review Matters">
        <div className="highlight-box"><strong> This is the single most important quality-control mechanism in the platform.</strong></div>
        <p>After a Carer accepts a booking (status: <em>accepted_by_carer</em>), the booking enters an <strong>admin approval gate</strong>. No Carer gets locked into a booking without an admin reviewing and confirming the match. This means:</p>
        <ul>
          <li><strong>For Care Seekers:</strong> Every Carer has been vetted by a human operator before being assigned. This builds trust.</li>
          <li><strong>For Carers:</strong> Once assigned, the booking is guaranteed — no last-minute competition from other Carers.</li>
          <li><strong>For Admin:</strong> The gate allows admin to reject unsuitable matches, reassign if needed, and maintain service quality.</li>
          <li><strong>For the Platform:</strong> This managed marketplace model is what differentiates B4T from unregulated freelance services.</li>
        </ul>
      </Card>

      {/* ===== SECTION 4: REVENUE & BUSINESS ===== */}
      <div className="section-hdr"> 4. Revenue & Business Model <span className="tag gold">How we make money</span></div>

      <Card id="revenue" isOpen={openCards["revenue"]} onToggle={toggleCard} dot="gold" title="Revenue Streams">
        <div className="grid-3">
          <div className="stat"><div className="num">1</div><div className="label">Booking Charge</div></div>
          <div className="stat"><div className="num">2</div><div className="label">Admin Fees</div></div>
          <div className="stat"><div className="num">3</div><div className="label">Commission %</div></div>
        </div>
        <table>
          <thead>
            <tr><th>Revenue Stream</th><th>How It Works</th><th>Who Pays</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Platform Booking Charge</strong></td><td>A fixed fee added to every booking's total. Configured as platform_booking_charge (default: 10% of subtotal).</td><td>Care Seeker at checkout</td></tr>
            <tr><td><strong>Admin Charges</strong></td><td>Additional configurable fees per booking. Collected as part of the total payment.</td><td>Care Seeker at checkout</td></tr>
            <tr><td><strong>Commission on Payouts</strong></td><td>When the Carer is paid (amount_distribute), the platform deducts a commission percentage. Commission rates are per plan type (Basic/Medium/Premium) and separately configurable for Admin and Carer.</td><td>Deducted from Carer payout</td></tr>
            <tr><td><strong>Promo Code Limitations</strong></td><td>Promo codes (percentage/fixed amount/fixed price) drive Care Seeker acquisition while being capped by maxRedemptions and endDate.</td><td>Platform absorbs discount</td></tr>
          </tbody>
        </table>
        <div className="highlight-box"><strong> Revenue equation:</strong> Care Seeker pays (subtotal + booking_charge + admin_charges - discount) → Platform keeps (booking_charge + admin_charges + commission%) → Carer receives (subtotal - commission%). Commission rates are fully configurable at runtime via Admin Dashboard.</div>
      </Card>

      <Card id="acquisition" isOpen={openCards["acquisition"]} onToggle={toggleCard} dot="gold" title="Customer Acquisition Channels">
        <div className="grid-2">
          <div>
            <strong>Customer Acquisition:</strong>
            <ul>
              <li><strong>App Store Organic</strong> — SEO for "airport assistance" and "travel Carer" keywords</li>
              <li><strong>Website (b4t.com)</strong> — Marketing landing page with lead capture form</li>
              <li><strong>Agent Partners</strong> — Travel agents, insurance companies, corporate travel desks refer bookings via affiliate links</li>
              <li><strong>Referral Program</strong> — Existing users share referral codes → referred user books → referrer gets reward</li>
              <li><strong>Social Media</strong> — Targeted ads for elderly Care Seeker segments, parents of unaccompanied minors</li>
              <li><strong>Partnerships</strong> — Airlines, travel insurance providers, senior travel groups</li>
            </ul>
          </div>
          <div>
            <strong>Carer/Agent Acquisition:</strong>
            <ul>
              <li><strong>App Store Organic</strong> — "Earn money at the airport" positioning</li>
              <li><strong>Word of Mouth</strong> — Airport community, travel industry networks</li>
              <li><strong>Agent Portal</strong> — Travel agents register via partner.b4t.com</li>
              <li><strong>Partner Referral</strong> — Existing agents refer new agents</li>
              <li><strong>Flexible Work</strong> — Key value prop: earn money on flights you're already taking</li>
              <li><strong>Retention via Tiers</strong> — Better rates = longer engagement</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* ===== SECTION 5: COMPETITIVE ===== */}
      <div className="section-hdr"> 5. Competitive Landscape <span className="tag rose">Why B4T wins</span></div>

      <Card id="competitors" isOpen={openCards["competitors"]} onToggle={toggleCard} dot="rose" title="Competitor Comparison">
        <table>
          <thead>
            <tr><th>Competitor Type</th><th>Example</th><th>B4T Advantage</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Airline wheelchair/escort</strong></td><td>Free service from airlines</td><td>Limited to airline staff availability, no in-flight support, no real-time tracking. B4T: dedicated Carer, full journey, live GPS tracking.</td></tr>
            <tr><td><strong>Airport concierge</strong></td><td>Luxury VIP services</td><td>Expensive ($200+/hr), luxury-focused, not flight-matched. B4T: affordable, flight-matched, transparent pricing.</td></tr>
            <tr><td><strong>Freelance Carers</strong></td><td>Unregulated individuals</td><td>No insurance, no tracking, no quality control. B4T: KYC-verified, GPS-tracked, admin-quality-controlled, rated marketplace.</td></tr>
            <tr><td><strong>Travel insurance add-ons</strong></td><td>Reactive coverage</td><td>Reactive (file a claim after incident), not proactive. B4T: real-time proactive assistance with chat, photo proof, live updates.</td></tr>
          </tbody>
        </table>
      </Card>

      <Card id="advantages" isOpen={openCards["advantages"]} onToggle={toggleCard} dot="rose" title="Key Competitive Advantages">
        <div className="grid-2">
          <div><strong> End-to-end journey coverage</strong><br /><span style={{ fontSize: "10px", color: "#94a3b8" }}>Not just a greeter — the Carer stays through every airport touchpoint, including in-flight for Premium.</span></div>
          <div><strong> Real-time tracking</strong><br /><span style={{ fontSize: "10px", color: "#94a3b8" }}>Both parties see journey progress live with GPS + photo proof at every stage. No other service offers this.</span></div>
          <div><strong> Admin quality control</strong><br /><span style={{ fontSize: "10px", color: "#94a3b8" }}>The approval gate ensures no Carer is assigned without human review. Managed marketplace, not a directory.</span></div>
          <div><strong> KYC-verified Carers</strong><br /><span style={{ fontSize: "10px", color: "#94a3b8" }}>Every Carer goes through Shufti Pro identity verification (government ID + facial comparison) before they can work.</span></div>
          <div><strong> Flight-matched Carers</strong><br /><span style={{ fontSize: "10px", color: "#94a3b8" }}>Carers are on the same flight as the Care Seeker — not just airport staff who disappear after security.</span></div>
          <div><strong> Multiple booking channels</strong><br /><span style={{ fontSize: "10px", color: "#94a3b8" }}>Book via Mobile App, Website, or through Agent Partners — maximum reach and convenience.</span></div>
        </div>
      </Card>

      {/* ===== SECTION 6: TECHNOLOGY ===== */}
      <div className="section-hdr"> 6. Technology Stack <span className="tag emerald">What powers the platform</span></div>

      <Card id="tech_arch" isOpen={openCards["tech_arch"]} onToggle={toggleCard} dot="emerald" title="Architecture Overview">
        <pre style={{ background: "#f3f4f6", padding: "12px", borderRadius: "6px", fontSize: "10px", overflowX: "auto" }}>
{`Consumer Mobile (React Native) ──→ Consumer API (Node.js) ──→ MongoDB
│                                      │
Public Website (Laravel/Vue) ───────────┘ ├── Shared PostgreSQL
                                       │
Admin Dashboard (React/MUI) ───────────────────┘ └── Business API (Laravel) ──→ MySQL + Redis`}
        </pre>
        <p><strong>Key architectural decisions:</strong></p>
        <ul>
          <li><strong>Single monorepo</strong> at <code>b4t/</code> — all 7 repos under one roof for simplified development</li>
          <li><strong>Shared PostgreSQL</strong> between Consumer API and Business API ensures real-time data consistency</li>
          <li><strong>Static token proxy</strong> — server-to-server authentication via pre-shared tokens for cross-service communication</li>
          <li><strong>Socket.IO</strong> for real-time chat with receipt tracking (sent → delivered → read)</li>
          <li><strong>Stripe</strong> for all payment processing including PaymentIntents, refunds, and payouts</li>
        </ul>
      </Card>

      <Card id="external" isOpen={openCards["external"]} onToggle={toggleCard} dot="emerald" title="External Service Dependencies">
        <table>
          <thead>
            <tr><th>Service</th><th>Purpose</th><th>Integration Type</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Stripe</strong></td><td>Payment processing, refunds, payouts</td><td>SDK v17 REST API</td></tr>
            <tr><td><strong>Shufti Pro</strong></td><td>KYC identity verification (document scan + facial comparison)</td><td>REST API + webhook callback</td></tr>
            <tr><td><strong>Firebase Cloud Messaging</strong></td><td>Push notifications to all mobile apps</td><td>Admin SDK + client SDKs</td></tr>
            <tr><td><strong>FlightAware</strong></td><td>Real-time flight data, route validation</td><td>AeroAPI REST</td></tr>
            <tr><td><strong>Mailtrap / SendGrid</strong></td><td>Transactional email (OTP, confirmations, receipts)</td><td>SMTP + REST API</td></tr>
            <tr><td><strong>AWS S3</strong></td><td>File storage (profile pics, ID proofs, journey photos)</td><td>AWS SDK v2 + Multer-S3</td></tr>
            <tr><td><strong>Sirv CDN</strong></td><td>Content delivery network for uploaded assets</td><td>Custom token + URL transform</td></tr>
            <tr><td><strong>Twilio</strong></td><td>SMS for OTP delivery</td><td>REST API</td></tr>
            <tr><td><strong>OpenAI + Pinecone</strong></td><td>AI support assistant knowledge base</td><td>REST API + vector DB</td></tr>
          </tbody>
        </table>
      </Card>

      <Card id="db_arch" isOpen={openCards["db_arch"]} onToggle={toggleCard} dot="emerald" title="Database Architecture">
        <table>
          <thead>
            <tr><th>Database</th><th>Technology</th><th>Key Data</th><th>Approx. Collections/Tables</th></tr>
          </thead>
          <tbody>
            <tr><td><code>buddy4travel</code></td><td>MongoDB</td><td>Users, Bookings, Chats, Wallets</td><td>45+ collections</td></tr>
            <tr><td><code>partner_b4t</code></td><td>PostgreSQL</td><td>Shared agent/partner data</td><td>Shared</td></tr>
            <tr><td><code>b4t_agent_portal</code></td><td>MySQL</td><td>Agent profiles, leads, rewards</td><td>30+ tables</td></tr>
          </tbody>
        </table>
        <p><span className="badge rose"></span> PostgreSQL is accessed by BOTH Consumer API (Node.js) and Business API (Laravel).</p>
      </Card>

      {/* ===== SECTION 7: CARE SEEKER & AGENT SEGMENTS ===== */}
      <div className="section-hdr"> 7. Care Seeker & Agent Segments <span className="tag">Who uses the platform</span></div>

      <Card id="roles" isOpen={openCards["roles"]} onToggle={toggleCard} dot="cyan" title="All User Roles">
        <table>
          <thead>
            <tr><th>Role</th><th>App Used</th><th>Key Action</th><th>Needs</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Care Seeker</strong></td><td>Consumer Mobile</td><td>Book airport assistance, pay, track journey</td><td>Trust, transparency, real-time updates</td></tr>
            <tr><td><strong>Carer (Carer)</strong></td><td>Agent Mobile</td><td>Accept bookings, execute trips, get paid</td><td>Flexible schedule, KYC verification, reliable payouts</td></tr>
            <tr><td><strong>Agent Partner</strong></td><td>Agent Mobile + Web Portal</td><td>Submit leads, earn rewards, track conversions</td><td>Lead management, affiliate links, reward visibility</td></tr>
            <tr><td><strong>Admin / Super Admin</strong></td><td>Admin Dashboard</td><td>Assign Carers, manage content, process payments</td><td>Complete visibility, granular permissions, audit trail</td></tr>
            <tr><td><strong>Sub-Admin</strong></td><td>Admin Dashboard</td><td>Limited operations per granted permissions</td><td>Role-based access, module-level permission control</td></tr>
            <tr><td><strong>Website Visitor</strong></td><td>Public Website</td><td>Browse, submit lead form</td><td>Clear value proposition, easy download CTA</td></tr>
          </tbody>
        </table>
      </Card>

      {/* ===== SECTION 8: ROADMAP ===== */}
      <div className="section-hdr"> 8. Roadmap & Future <span className="tag">Where we're going</span></div>

      <Card id="roadmap" isOpen={openCards["roadmap"]} onToggle={toggleCard} dot="sky" title="Strategic Priorities">
        <div className="grid-2">
          <div>
            <strong>Near-term (in development):</strong>
            <ul>
              <li>AI Support Assistant — OpenAI + Pinecone knowledge base for automated Care Seeker support</li>
              <li>Advanced Analytics Dashboard — Google Analytics Data API integration for deeper business insights</li>
              <li>Multi-currency wallet improvements — better exchange rate handling and currency support</li>
              <li>Onboarding flow optimization — reduce friction in registration and first booking</li>
            </ul>
          </div>
          <div>
            <strong>Strategic opportunities:</strong>
            <ul>
              <li>International expansion — multi-language, multi-currency infrastructure already partially in place</li>
              <li>Corporate/enterprise portal — dedicated booking for corporate travel desks</li>
              <li>Travel insurance integration — direct partnership with insurance providers</li>
              <li>Airline API partnership — direct integration with airline booking systems</li>
              <li>White-label solution — package the platform for travel brands to offer as their own service</li>
            </ul>
          </div>
        </div>
      </Card>

      <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "40px 0" }} />

      {/* ===== SECTION 7: PLATFORM RULES & POLICIES ===== */}
      <div className="section-hdr"> 7. Platform Rules & Policies <span className="tag rose">Eligibility, privacy & matching</span></div>

      <Card id="age_rules" isOpen={openCards["age_rules"]} onToggle={toggleCard} dot="amber" title="Age Eligibility Rules">
        <div className="grid-2">
          <div>
            <strong>Carers (Care Providers):</strong>
            <ul>
              <li>Minimum age: <strong>18 years</strong></li>
              <li>Maximum age: <strong>55 years</strong></li>
              <li>Only users within this range may register or act as a Carer</li>
            </ul>
          </div>
          <div>
            <strong>Care Seekers:</strong>
            <ul>
              <li>A Care Seeker may be a minor</li>
              <li>A minor Care Seeker must be accompanied by an adult</li>
              <li>There is currently <strong>no maximum age limit</strong> for Care Seekers</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card id="matching" isOpen={openCards["matching"]} onToggle={toggleCard} dot="emerald" title="Matching Rules & Booking Preferences">
        <p>Users cannot directly choose a specific individual Carer or Care Seeker. However, during booking creation, users may select certain preferences:</p>
        <div className="grid-2">
          <div>
            <strong>Allowed Preferences:</strong>
            <ul>
              <li>Preferred gender of Carer</li>
              <li>Preferred profession or skill set (e.g. nurse, multilingual, mobility specialist)</li>
              <li>Language preferences with proficiency level</li>
              <li>Medical needs or conditions</li>
            </ul>
            <p style={{ fontSize: "10px", color: "#6b7280", marginTop: "6px" }}>These preferences are optional, improve comfort and safety, do not guarantee availability, and are only used for matching purposes.</p>
          </div>
          <div>
            <strong>Not Allowed:</strong>
            <ul>
              <li>Requesting a specific named Carer or Care Seeker</li>
              <li>Requiring someone from a specific race, ethnicity, nationality, religion, age group, or appearance</li>
              <li>Submitting discriminatory or personal-bias requests</li>
            </ul>
            <p style={{ fontSize: "10px", color: "#e11d48", marginTop: "6px" }}>Such requests violate B4T Terms of Service.</p>
          </div>
        </div>
        <div className="highlight-box"><strong>Matching Process:</strong> (1) User submits booking with optional preferences → (2) B4T matches based on availability, route, skills, and preferences → (3) Both parties may accept or reject each other → (4) If either declines, a new match is attempted based on availability.</div>
      </Card>

      <Card id="privacy" isOpen={openCards["privacy"]} onToggle={toggleCard} dot="cyan" title="Data Privacy — What We Share & What We Don't">
        <p>B4T follows strict global privacy and data-minimisation standards (Privacy Act 1988 (Cth) and GDPR compliant).</p>
        <div className="grid-2">
          <div>
            <strong>Data shared between Carer and Care Seeker:</strong>
            <ul>
              <li>Only the <strong>Name</strong></li>
              <li>Shared only <strong>after both users accept each other</strong> before the journey begins</li>
            </ul>
            <strong>Data NOT shared:</strong>
            <ul>
              <li>Phone number, email address</li>
              <li>Personal/demographic information</li>
              <li>Sensitive attributes</li>
              <li>All communication must happen through the in-app chat</li>
            </ul>
          </div>
          <div>
            <strong>Data collected by B4T:</strong>
            <ul>
              <li>Full name, email, phone, address</li>
              <li>Government ID, flight tickets, boarding pass</li>
              <li>Health background, date of birth, gender</li>
              <li>Languages, occupation, nationality</li>
              <li>Payment info, GPS location</li>
            </ul>
            <strong>Contact for data requests:</strong>
            <ul>
              <li>Access/deletion: <code>contact@b4t.com</code></li>
              <li>Unsubscribe: <code>marketing@b4t.com</code></li>
            </ul>
          </div>
        </div>
      </Card>

      <Card id="carer_notes" isOpen={openCards["carer_notes"]} onToggle={toggleCard} dot="rose" title="Carer Important Notes">
        <ul>
          <li><strong>Medicine assistance:</strong> Carer only provides assistance — all medicines must be supplied by the Care Seeker. The Carer does not administer injections.</li>
          <li><strong>Wheelchair assistance:</strong> Subject to airport and airline approval (Premium only).</li>
          <li>Carer is <strong>NOT responsible</strong> for the contents of the Care Seeker's luggage. Carer only assists with opening and closing luggage.</li>
          <li>Keep all travel documents, valuables, and personal belongings with the Care Seeker at all times. <strong>B4T is not liable for theft or loss of personal property.</strong></li>
          <li>All communication with Care Seekers must happen through the <strong>in-app chat</strong> — do not share personal contact details.</li>
        </ul>
      </Card>

    </DocWrapper>
  );
}
